import {Suspense, useEffect, useState} from "react";
import {Helmet} from "react-helmet-async";
import {useSearchParams} from "react-router";

import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {redirectIfUnavailableFabric} from "@/lib/handlers/utils.tsx";
import {useTable} from "@/lib/hooks/useTable.ts";
import {AuditLogObjectTypeDisplay} from "@/lib/types/auditlogs.ts";
import {AuditLogActionBar} from "@/pages/auditlog/list/components/AuditLogActionBar.tsx";
import {AuditLogList} from "@/pages/auditlog/list/components/AuditLogList.tsx";

const OBJECT_TYPE_KEY = "objectType";

export const AuditLog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {search, searchDebounced, searchChanged, pagination, setPagination} = useTable();
  const urlObjectTypeFilter = searchParams.get(OBJECT_TYPE_KEY) as keyof typeof AuditLogObjectTypeDisplay | null;
  const [objectTypeFilter, setObjectTypeFilter] = useState<keyof typeof AuditLogObjectTypeDisplay | null>(
    urlObjectTypeFilter
  );

  // Back and links change the URL: follow it in the same render as the page and search, which sync the same way.
  useEffect(() => {
    setObjectTypeFilter(urlObjectTypeFilter);
  }, [urlObjectTypeFilter]);

  return (
    <ContentContainer
      title="Audit Log"
      actionsBar={
        <AuditLogActionBar
          search={search}
          setSearch={searchChanged}
          objectTypeFilter={objectTypeFilter}
          setObjectTypeFilter={(value) => {
            setObjectTypeFilter(value);
            const params = new URLSearchParams(searchParams);
            if (value) {
              params.set(OBJECT_TYPE_KEY, value);
            } else {
              params.delete(OBJECT_TYPE_KEY);
            }
            setPagination({...pagination, offset: 0}, params);
            setSearchParams(params);
          }}
        />
      }
    >
      <Helmet>
        <title>Audit Log</title>
      </Helmet>
      <UpErrorBoundary onError={redirectIfUnavailableFabric("audit_log")}>
        <Suspense>
          <AuditLogList
            limitAndOffset={pagination}
            setLimitAndOffset={setPagination}
            search={searchDebounced}
            objectTypeFilter={objectTypeFilter}
          />
        </Suspense>
      </UpErrorBoundary>
    </ContentContainer>
  );
};
